import React, { useEffect, useState } from "react";
import Header from "../home/header";
import feature from "../../img/suppliers.svg";
import { useLocation, useNavigate, useParams } from "react-router";
import { X, Pen } from "lucide-react";
import Login from "../home/login_form";
import { ApiClient } from "../../utils/api-client";
import UTILS from "../../utils/commons";
import DocumentPreview from "../home/document_preview";

const SupplierDetail = ( props ) => {
  let navigate = useNavigate();
  var location = useLocation();
  const loginState = JSON.parse( window.sessionStorage.getItem( "loginState" ) );
  const apiClient = new ApiClient( loginState ? loginState.accessToken : null );
  var { id } = useParams();
  const [ supplierData, setSupplierData ] = useState( location.state || {} );
  const [ recentInvoices, setRecentInvoices ] = useState( [] );
  const [ loadingInvoices, setLoadingInvoices ] = useState( false );
  const [ debtSummary, setDebtSummary ] = useState( { totalInvoiced: 0, totalPaid: 0, debt: 0 } );

  const supplier = supplierData || {};

  const loadSupplier = async () => {
    if ( location.state && location.state.id ) {
      setSupplierData( location.state );
      return;
    }

    try {
      const response = await apiClient.get( `suppliers/${id}` );
      const payload = response?.data?.data || response?.data || {};
      setSupplierData( payload );
    }
    catch ( error ) {
      console.error( "Failed to load supplier details:", error );
    }
  }

  const loadRecentInvoices = async () => {
    const supplierId = location.state?.id || id;
    if ( !supplierId ) return;

    setLoadingInvoices( true );
    try {
      const response = await apiClient.get( "inventory", { params: { supplierId } } );
      const rawRows = Array.isArray( response?.data?.data )
        ? response.data.data
        : Array.isArray( response?.data )
          ? response.data
          : [];

      // Keep only rows for the active supplier to avoid cross-supplier totals.
      const rows = rawRows.filter( row => row?.supplier?.id === supplierId || row?.supplierId === supplierId );

      const groupedByInvoice = rows.reduce( ( acc, row ) => {
        const invoiceNumber = row?.invoiceNumber || "N/A";
        const current = acc[ invoiceNumber ] || {
          invoiceNumber,
          createdAt: row?.createdAt || null,
          itemCount: 0,
          totalAmount: 0,
          cifCost: Number( row?.cifCost || 0 ),
          amountPaid: Number( row?.amountPaid || 0 ),
          debt: Math.max( Number( row?.cifCost || 0 ) - Number( row?.amountPaid || 0 ), 0 ),
          file: row?.invoiceFile || null,
          product: row?.product || null,
        };

        const rowDate = row?.createdAt ? new Date( row.createdAt ).getTime() : 0;
        const currentDate = current?.createdAt ? new Date( current.createdAt ).getTime() : 0;

        current.itemCount += 1;
        current.totalAmount += Number( row?.buyingPrice || 0 ) * Number( row?.stockQuantity || 0 );
        current.cifCost = Number( row?.cifCost || current.cifCost || 0 );
        current.amountPaid = Number( row?.amountPaid || current.amountPaid || 0 );
        current.debt = Math.max( current.cifCost - current.amountPaid, 0 );
        if ( rowDate > currentDate ) {
          current.createdAt = row.createdAt;
        }
        if ( !current.file && row?.invoiceFile ) {
          current.file = row.invoiceFile;
        }

        acc[ invoiceNumber ] = current;
        return acc;
      }, {} );

      const invoices = Object.values( groupedByInvoice );
      const totalInvoiced = invoices.reduce( ( sum, inv ) => sum + Number( inv.cifCost || 0 ), 0 );
      const totalPaid = invoices.reduce( ( sum, inv ) => sum + Number( inv.amountPaid || 0 ), 0 );
      setDebtSummary( {
        totalInvoiced,
        totalPaid,
        debt: Math.max( totalInvoiced - totalPaid, 0 )
      } );

      const sorted = invoices
        .sort( ( a, b ) => new Date( b.createdAt || 0 ).getTime() - new Date( a.createdAt || 0 ).getTime() )
        .slice( 0, 8 );

      setRecentInvoices( sorted );
    }
    catch ( error ) {
      console.error( "Failed to load recent supplier invoices:", error );
      setRecentInvoices( [] );
      setDebtSummary( { totalInvoiced: 0, totalPaid: 0, debt: 0 } );
    }
    setLoadingInvoices( false );
  }

  useEffect( () => {
    if ( !loginState ) return;
    loadSupplier();
    loadRecentInvoices();
  }, [ id ] );

  const detailCards = [
    { label: "Company Name", value: supplier.companyName || "N/A" },
    { label: "Country", value: supplier.country || "N/A" },
    { label: "Address", value: supplier.address || "N/A" },
    { label: "Contact Person", value: supplier.contactName || "N/A" },
    { label: "Phone", value: supplier.contactPhone || "N/A" },
    { label: "E-mail", value: supplier.contactEmail || "N/A" },
  ];

  if ( loginState ) {
    return ( <>
      <Header auth={loginState} />
      <section className="container justify-start mt-8">

        <div className="rounded-xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 shadow-sm p-4 md:p-6">
          <div className="w-full mb-4 flex justify-between items-start gap-4">
            <div>
              <p className="text-sm uppercase tracking-wide text-gray-500">Procurement</p>
              <div className="text-2xl font-semibold text-gray-800">Supplier Details</div>
              <p className="text-sm text-gray-500 mt-1">Reference ID: {supplier.id || id}</p>
            </div>
            <div>
              <button className="rounded-md p-2 hover:bg-gray-100" onClick={() => { navigate( "/suppliers/edit/" + id, { replace: true, state: supplier } ) }}><Pen className="text-primary-500 hover:text-secondary-500" /></button>
              <button className="rounded-md p-2 hover:bg-gray-100" onClick={() => { navigate( -1, { replace: true } ) }}><X className="text-primary-500 hover:text-secondary-500" /></button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-4">
            <div className="lg:col-span-4 rounded-lg border border-gray-200 bg-white p-6 flex flex-col justify-center items-center text-center">
              <img id="preview" alt={supplier.companyName || "Supplier"} src={feature} className="w-28 md:w-36" />
              <p className="mt-4 text-lg font-semibold text-gray-800">{supplier.companyName || "Unknown Supplier"}</p>
              <p className="text-sm text-gray-500">{supplier.country || "No country recorded"}</p>
            </div>

            <div className="lg:col-span-8 rounded-lg border border-gray-200 bg-white p-4 md:p-5">
              <p className="text-sm uppercase tracking-wide text-gray-500 mb-4">Supplier Information</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {detailCards.map( ( item ) => (
                  <div key={item.label} className="rounded-md border border-gray-100 bg-gray-50 p-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">{item.label}</p>
                    <p className="text-base text-gray-800 font-medium mt-1">{item.value}</p>
                  </div>
                ) )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Total Invoiced (TZS)</p>
                  <p className="text-base text-secondary-700 font-semibold mt-1">{UTILS.thousandSeparator( debtSummary.totalInvoiced )}</p>
                </div>
                <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Total Paid (TZS)</p>
                  <p className="text-base text-secondary-700 font-semibold mt-1">{UTILS.thousandSeparator( debtSummary.totalPaid )}</p>
                </div>
                <div className="rounded-md border border-red-100 bg-red-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-red-500">Amount Owed (TZS)</p>
                  <p className="text-base text-red-700 font-semibold mt-1">{UTILS.thousandSeparator( debtSummary.debt )}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 md:p-5 mt-6">
          <div className="text-xl font-semibold text-gray-800">Recent Invoices</div>
          {loadingInvoices ? (
            <div className="mt-3 text-gray-500">Loading invoices...</div>
          ) : recentInvoices.length > 0 ? (
            <div className="mt-4 overflow-x-auto">
              <div className="min-w-[680px]">
                <div className="grid grid-cols-5 bg-gray-100 rounded-t-md border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700">
                  <span>Date</span>
                  <span>Invoice Number</span>
                  <span>Product</span>
                  <span>Invoice Amount</span>
                  <span>Document</span>
                </div>
                {recentInvoices.map( inv => (
                  <div key={inv.invoiceNumber} className="grid grid-cols-5 border-x border-b border-gray-200 px-3 py-2 text-sm text-gray-700">
                    <span>{UTILS.formatDate( inv.createdAt )}</span>
                    <span>{inv.invoiceNumber}</span>
                    <span>{inv.product?.productName} - {inv.product?.packSize}</span>
                    <span className="font-medium">{UTILS.thousandSeparator( inv.cifCost )}</span>
                    <span>
                      {inv.file ? <DocumentPreview fileData={inv.file} fileName={`${inv.invoiceNumber || 'invoice'}_document`} buttonText="View File" /> : "N/A"}
                    </span>
                  </div>
                ) )}
              </div>
            </div>
          ) : (
            <div className="mt-3 text-gray-500">No recent invoices</div>
          )}
        </div>

      </section>
    </> )
  }
  else return <Login target="/suppliers" />
}
export default SupplierDetail;